import {
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { UserDataContactModel } from 'src/lib/services/api/users/user-data.model';

import { KeyValuePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
	AsyncSubject,
	debounceTime,
	filter,
	Observable,
	of,
	switchMap,
	takeUntil,
} from 'rxjs';
import {
	PROTECTED_EMAIL_DOMAINS,
	ValidationConstants,
} from 'src/lib/constants/constants';
import { Permits } from 'src/lib/constants/permissions';
import { AddressValidationService } from 'src/lib/services/api/generic/address/address-validation.service';
import { StudentContactPhoneArgument } from 'src/lib/services/api/students/contacts/student-contact.argument';
import {
	ContactFieldMetadata,
	ContactFieldPhoneModel,
	ContactFieldValueModel,
} from 'src/lib/services/api/users/contact-field.model';
import { UserUpdateRequestArgument } from 'src/lib/services/api/users/update-request/users-update-request.argument';
import {
	ProofRequirementModel,
	UserUpdateRequestOptions,
} from 'src/lib/services/api/users/update-request/users-update-request.model';
import { UsersUpdateRequestsService } from 'src/lib/services/api/users/update-request/users-update-requests.service';
import { PermissionStoreService } from 'src/lib/services/stores/permission-store/permission-store.service';
import { UserAddressModel } from 'src/lib/services/utility/utility-models/address.model';
import { AddressFormComponent } from 'src/lib/templates/controls/address-form/address-form.component';
import { FormLabelComponent } from 'src/lib/templates/global/form-label/form-label.component';
import { GroupValidationModule } from 'src/lib/templates/layout/group-validation/group-validation.module';
import { FormControlWrapper } from 'src/lib/types/forms.def';
import { MapResponseErrors } from 'src/lib/utilities/api/map-response';
import { mergeStrings } from 'src/lib/utilities/array';
import {
	containsString,
	getChangedValues,
	hasKey,
	hasSomeKey,
	hasValue,
	isEquivalentObject,
} from 'src/lib/utilities/compare';
import {
	commonValidators,
	createAddressGroup,
	forceUpdateStatus,
	markFormGroupTouched,
} from 'src/lib/utilities/forms';
import { isSameAddress } from 'src/lib/utilities/gradally';
import { UploadProofModalService } from 'src/lib/views/modals/proof-modal/upload-proof-modal.service';
import { InputCheckboxComponent } from '../../../../../templates/controls/input-checkbox/input-checkbox.component';
import { IconTooltipComponent } from '../../../../../templates/global/icon-tooltip/icon-tooltip.component';

export interface ProfileContactEditCalendarFormGroup {
	link: string;
}

interface SsoEmailFormGroup {
	originalItem: ContactFieldValueModel;
	newValue: string;
	delete: boolean;
}

class ProfileContactFormModel {
	phone: FormArray<FormGroup<FormControlWrapper<StudentContactPhoneArgument>>>;
	mailing_address: FormGroup<FormControlWrapper<UserAddressModel>>;
	physical_address: FormGroup<FormControlWrapper<UserAddressModel>>;
	calendar: FormControl<string>;
	ssoEmail: FormArray<FormGroup<FormControlWrapper<SsoEmailFormGroup>>>;
}

@Component({
	selector: 'ae-profile-contact-edit',
	templateUrl: './profile-contact-edit.component.html',
	styleUrls: ['./profile-contact-edit.component.scss'],
	imports: [
		AddressFormComponent,
		FormLabelComponent,
		FormsModule,
		GroupValidationModule,
		IconTooltipComponent,
		InputCheckboxComponent,
		KeyValuePipe,
		NgbTooltip,
		NgClass,
		NgTemplateOutlet,
		ReactiveFormsModule,
	],
})
export class ProfileContactEditComponent implements OnInit, OnDestroy {
	private _unsubscribe$ = new AsyncSubject<null>();

	@Input() public currentUserId: number;
	@Input() public contactData: UserDataContactModel;
	@Input() public phoneOptions: Map<string, string>;
	@Input() public contactOptions: UserUpdateRequestOptions;
	@Input() public contactSaveListener: (forceRefresh: boolean) => void;

	@ViewChild('rootElement', { static: true })
	rootElement: ElementRef<HTMLElement>;

	public savingInfo: boolean = false;

	public profileContactFormGroup: FormGroup<ProfileContactFormModel>;

	protected toggleAddressCtrl = new FormControl<boolean>(false);

	public primaryPhoneIndex: number = 0;
	public zipRegex: string = ValidationConstants.zipcode;
	public canEditSSOEmail: boolean = false;
	public hasChanges: boolean = false;
	private toggleAddressOriginalValue: boolean = false;
	private canBypassUpdateProofRequirement: boolean = false;
	protected proofMap = new Map<string, ProofRequirementModel>();
	private originalForm;

	public hasKey = hasKey;
	public hasSomeKey = hasSomeKey;

	constructor(
		private fb: FormBuilder,
		private userRequestService: UsersUpdateRequestsService,
		private addressValidationService: AddressValidationService,
		private uploadProofModalService: UploadProofModalService,
		private toastr: ToastrService,
		private permissionsService: PermissionStoreService,
	) {}

	ngOnInit() {
		// get indexes of primary values
		this.primaryPhoneIndex = this.contactData?.phone.findIndex((val) => {
			return val.prim;
		});

		this.profileContactFormGroup = this.fb.group<ProfileContactFormModel>({
			phone: this.fb.array<
				FormGroup<FormControlWrapper<StudentContactPhoneArgument>>
			>([]),
			calendar: this.fb.control<string | null>(null, [
				commonValidators.requiredNotEmpty,
				commonValidators.url(),
			]),
			ssoEmail: this.fb.array<FormGroup<FormControlWrapper<SsoEmailFormGroup>>>(
				[],
			),
			mailing_address: this.buildAddressGroup(
				this.contactData?.mailing_address
					? this.contactData?.mailing_address
					: this.contactData?.physical_address,
			),
			physical_address: this.buildAddressGroup(
				this.contactData?.physical_address,
			),
		});

		// disable calendar if no value
		if (!(this.contactData?.messenger?.length > 0)) {
			this.profileContactFormGroup.controls.calendar.disable();
		}

		const ctrls = this.profileContactFormGroup.controls;

		this.contactData?.phone?.forEach((phone) => {
			ctrls.phone.push(this.buildPhoneGroup(phone));
		});

		// Currently showing only one Messenger item to edit, but system is built for multiple use (if we want to in the future)
		if (this.contactData?.messenger?.length > 0) {
			ctrls.calendar.setValue(this.contactData?.messenger[0]?.value);
		}

		if (
			!hasKey(this.contactOptions?.contact_field?.physical_address, 'subfields')
		) {
			ctrls.physical_address.disable();
		}

		if (
			!hasKey(this.contactOptions?.contact_field?.mailing_address, 'subfields')
		) {
			ctrls.mailing_address.disable();
		}

		if (!hasKey(this.contactOptions?.contact_field, 'phone')) {
			ctrls.phone.disable();
		}

		if (!hasKey(this.contactOptions?.contact_field, 'messenger')) {
			ctrls.calendar.disable();
		}

		this.toggleAddressCtrl.valueChanges
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe({
				next: (val) => {
					if (val) {
						this.profileContactFormGroup.controls.mailing_address.setValue(
							this.profileContactFormGroup.controls.physical_address
								.value as UserAddressModel,
						);
						this.profileContactFormGroup.controls.mailing_address.disable();
					} else {
						this.profileContactFormGroup.controls.mailing_address.enable();
					}
				},
			});

		this.profileContactFormGroup.controls.physical_address.valueChanges
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((val) => {
				if (this.toggleAddressCtrl.value) {
					this.profileContactFormGroup.controls.mailing_address.setValue(
						val as UserAddressModel,
					);
				}
			});

		if (
			this.contactData?.mailing_address ===
				this.contactData?.physical_address ||
			!this.contactData?.mailing_address
		) {
			this.toggleAddressCtrl.setValue(true);
		}

		this.originalForm = this.profileContactFormGroup.value;

		this.toggleAddressOriginalValue = this.toggleAddressCtrl.value;

		this.profileContactFormGroup.valueChanges
			.pipe(debounceTime(1000))
			.subscribe(() => {
				this.hasChanges = !isEquivalentObject(
					this.profileContactFormGroup.value,
					this.originalForm,
				);
			});

		this.permissionsService
			.getFieldSet$()
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((p) => {
				this.canEditSSOEmail = p.canDo(
					Permits['ga_institute|edit program email for user'],
				);

				this.canBypassUpdateProofRequirement = p.canDo(
					Permits['ga_user|user update request|bypass proof requirement'],
				);

				this.contactData?.ssoEmail?.forEach((email) => {
					ctrls.ssoEmail.push(this.buildSsoEmailGroup(email));
				});
			});

		this.proofMap = this.getProofRequirements();
	}

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}

	public save = () => {
		//check changed addresses

		const physicalAddressCtrlVal =
			this.profileContactFormGroup.controls.physical_address.value;
		const mailingAddressCtrlVal = this.profileContactFormGroup.controls
			.mailing_address.enabled
			? this.profileContactFormGroup.controls.mailing_address.value
			: null;

		const originalPhysicalAddress = {
			street: this.contactData.physical_address?.street,
			street2: this.contactData.physical_address?.street2,
			city: this.contactData.physical_address?.city,
			state: this.contactData.physical_address?.state,
			zip: this.contactData.physical_address?.zip,
		};

		const originalMailingAddress =
			this.contactData.mailing_address == null
				? null
				: {
						street: this.contactData.mailing_address.street,
						street2: this.contactData.mailing_address.street2,
						city: this.contactData.mailing_address.city,
						state: this.contactData.mailing_address.state,
						zip: this.contactData.mailing_address.zip,
					};

		const physicalAddressCompare = isSameAddress(
			physicalAddressCtrlVal,
			originalPhysicalAddress,
		);

		const mailingAddressCompare = isSameAddress(
			mailingAddressCtrlVal,
			originalMailingAddress,
		);

		const addresses: FormGroup<FormControlWrapper<UserAddressModel>>[] = [];

		if (!mailingAddressCompare || !physicalAddressCompare) {
			addresses.push(this.profileContactFormGroup.controls.physical_address);
		}

		if (
			((!mailingAddressCompare && this.contactData.physical_address) ||
				this.toggleAddressOriginalValue !== this.toggleAddressCtrl.value) &&
			hasValue(
				this.profileContactFormGroup.controls.mailing_address.controls.street
					.value,
			)
		) {
			addresses.push(this.profileContactFormGroup.controls.mailing_address);
		}

		if (!this.savingInfo) {
			this.savingInfo = true;

			this.addressValidationService
				.verifyAddresses$({
					addressFormGroups: addresses,
				})
				.pipe(
					filter((verified) => verified),
					switchMap(() => {
						const controlGroup = this.profileContactFormGroup.controls;
						const profileContactArgs: UserUpdateRequestArgument = {
							contact_field: {},
						};

						const phoneArray = controlGroup.phone.controls.map(
							(ctrl) => ctrl.value,
						);

						const originalPhoneArray = this.contactData.phone?.map((phone) => {
							return {
								phone: phone.value,
								type: phone.type,
								sms: phone.sms,
							};
						});

						if (!isEquivalentObject(phoneArray, originalPhoneArray)) {
							profileContactArgs.contact_field.phone = [];
							controlGroup.phone.controls.forEach(
								(
									ctrl: FormGroup<
										FormControlWrapper<StudentContactPhoneArgument>
									>,
									index,
								) => {
									profileContactArgs.contact_field.phone.push({
										active: true,
										prim: this.primaryPhoneIndex === index ? true : false,
										sms: ctrl.controls.sms.value,
										type:
											ctrl.controls.type.value == null ||
											ctrl.controls.type.value === ''
												? null
												: ctrl.controls.type.value,
										value: ctrl.controls.phone.value.toString(),
										weight: index,
									});
								},
							);
						}

						if (!physicalAddressCompare) {
							profileContactArgs.contact_field.physical_address = {
								...(this.profileContactFormGroup.controls.physical_address
									.value as UserAddressModel),
								active: true,
							};
						}

						if (
							!mailingAddressCompare ||
							(this.toggleAddressOriginalValue !==
								this.toggleAddressCtrl.value &&
								this.profileContactFormGroup.controls.mailing_address.value
									.street)
						) {
							// Mailing address update always requires physical address to be updated
							profileContactArgs.contact_field.physical_address = {
								...(this.profileContactFormGroup.controls.physical_address
									.value as UserAddressModel),
								active: true,
							};

							profileContactArgs.contact_field.mailing_address = {
								...(this.profileContactFormGroup.controls.mailing_address
									.value as UserAddressModel),
								active: !this.toggleAddressCtrl.value,
							};
						}

						const calendarLink = controlGroup.calendar.value;

						const originalCalendarLink =
							this.contactData.messenger?.map((v) => v.value)?.[0] ?? null;

						if (calendarLink !== originalCalendarLink) {
							profileContactArgs.contact_field.messenger = [];
							profileContactArgs.contact_field.messenger = [
								{
									active: true,
									prim: true,
									type: 'list',
									weight: 0,
									value: calendarLink,
								},
							];
						}

						let emailChangeCount = 0;
						profileContactArgs.contact_field.email = [];
						controlGroup.ssoEmail.controls.forEach((ctrl) => {
							const item = ctrl.controls.originalItem.value;

							if (ctrl.controls.delete.value === true) {
								item.active = false;
								emailChangeCount++;
							} else if (item.value !== ctrl.controls.newValue.value) {
								item.value = ctrl.controls.newValue.value;
								emailChangeCount++;
							}
							profileContactArgs.contact_field.email.push(item);
						});

						// Make arrays go bye bye if empty
						if (emailChangeCount > 0) {
							profileContactArgs.contact_field.email.push(
								this.contactData.email.find(
									(email) => email.prim && email.active,
								),
							);
						} else {
							delete profileContactArgs.contact_field.email;
						}

						if (
							profileContactArgs.contact_field.messenger?.length < 1 &&
							(this.contactData.messenger?.length ?? 0) < 1
						) {
							delete profileContactArgs.contact_field.messenger;
						}

						let obs$: Observable<
							{
								ids: string[];
								file: File;
							}[]
						> = of([]);

						if (
							!this.originalForm.hasOwnProperty('mailing_address') &&
							this.profileContactFormGroup.value.hasOwnProperty(
								'mailing_address',
							)
						) {
							this.originalForm.mailing_address = null;
						}

						const changedValues = getChangedValues(
							this.originalForm,
							this.profileContactFormGroup.value,
						);
						const map = new Map<string, ProofRequirementModel>();

						if (changedValues && this.proofMap.size > 0) {
							changedValues.forEach((v) => {
								const p = this.proofMap.get(v.key);
								if (p?.required && !this.canBypassUpdateProofRequirement) {
									map.set(v.key, this.proofMap.get(v.key));
								}
							});
							if (map.size > 0) {
								obs$ = this.proofModal$(map);
							}
						}

						return obs$.pipe(
							takeUntil(this._unsubscribe$),
							filter((x) => x != null),
							switchMap((proofFiles) => {
								return this.userRequestService.createUpdateRequest(
									this.currentUserId,
									profileContactArgs,
									proofFiles,
									this.canBypassUpdateProofRequirement,
								);
							}),
						);
					}),
				)
				.subscribe({
					next: (response: { success; update_request; error_messages }) => {
						if (response.success) {
							if (response.update_request) {
								this.toastr.success(
									'Contact info update has been requested. Your changes will not be shown until they are approved.',
								);
							} else {
								this.toastr.success('Contact info has been updated.');
							}
							this.contactSaveListener(true);
						} else {
							const msg = response.error_messages?.join('\n') ?? '';
							this.toastr.error(`Error saving changes: ${msg}`);
						}
					},
					error: (error: MapResponseErrors) => {
						this.toastr.error(`Error saving changes: ${mergeStrings(error)}`);
					},
					complete: () => {
						this.savingInfo = false;
					},
				});
		}
	};

	protected addPhone = () => {
		if (this.profileContactFormGroup.controls.phone.length === 0) {
			this.primaryPhoneIndex = 0;
		}
		this.profileContactFormGroup.controls.phone.push(this.buildPhoneGroup());
	};

	protected deletePhone = (index: number) => {
		this.profileContactFormGroup.controls.phone.removeAt(index);
		if (index === this.primaryPhoneIndex) {
			this.primaryPhoneIndex = 0;
		} else {
			this.primaryPhoneIndex -= 1;
		}
	};

	private buildPhoneGroup = (phoneArgs?: ContactFieldPhoneModel) => {
		const ctrl = this.fb.group<FormControlWrapper<StudentContactPhoneArgument>>(
			{
				phone: new FormControl(phoneArgs ? phoneArgs.value : null, [
					commonValidators.phone,
					commonValidators.notSevenDigitPhone,
					Validators.required,
				]),
				type: new FormControl(phoneArgs ? phoneArgs.type : null),
				sms: new FormControl(
					phoneArgs ? phoneArgs.sms : true,
					Validators.required,
				),
			},
		);

		if (phoneArgs) {
			markFormGroupTouched(ctrl);
			forceUpdateStatus(ctrl);
		}

		return ctrl;
	};

	public addCalendar = () => {
		this.profileContactFormGroup.controls.calendar.enable();
	};

	public deleteCalendar = () => {
		this.profileContactFormGroup.controls.calendar.disable();
	};

	private buildAddressGroup = (
		addressArgs?: UserAddressModel & Partial<ContactFieldMetadata>,
	) => {
		const ctrl = createAddressGroup(
			addressArgs instanceof Array ? null : addressArgs,
		);

		if (addressArgs) {
			markFormGroupTouched(ctrl);
			forceUpdateStatus(ctrl);
		}

		return ctrl;
	};

	private buildSsoEmailGroup = (item: ContactFieldValueModel) => {
		const protectedDomain = PROTECTED_EMAIL_DOMAINS.find((domain) =>
			containsString(item.value, domain),
		);

		const ctrl = this.fb.group<FormControlWrapper<SsoEmailFormGroup>>({
			originalItem: new FormControl(item),
			newValue: new FormControl(
				{ value: item.value, disabled: !this.canEditSSOEmail },
				[commonValidators.emailAndLength],
			),
			delete: new FormControl({
				value: false,
				disabled: protectedDomain != null && !this.canEditSSOEmail,
			}),
		});

		if (protectedDomain != null) {
			ctrl.controls.newValue.addValidators(
				this.protectedEmailValidator(protectedDomain),
			);
		}

		markFormGroupTouched(ctrl);
		forceUpdateStatus(ctrl);

		return ctrl;
	};

	private protectedEmailValidator = (domain: string): ValidatorFn => {
		return (ctrl: AbstractControl): ValidationErrors | null => {
			if (!containsString(ctrl.value, domain)) {
				return {
					protectedEmailRequired: `${domain} is required when updating existing ${domain} emails`,
				};
			} else {
				return null;
			}
		};
	};

	public getSsoEmailDeleteTitle = (
		fg: FormGroup<FormControlWrapper<SsoEmailFormGroup>>,
	) => {
		if (fg == null) {
			return null;
		}

		if (fg.controls.delete.disabled) {
			return 'Cannot delete program email';
		}

		if (fg.controls.delete.value) {
			return 'Undo Delete';
		} else {
			return 'Delete';
		}
	};

	public setPrimaryPhone = (currentIndex: number) => {
		this.primaryPhoneIndex = currentIndex;
	};

	public showAddressError = (
		addressGroup: FormGroup<FormControlWrapper<UserAddressModel>>,
	) => {
		for (const c in addressGroup.controls) {
			if (addressGroup.controls.hasOwnProperty(c)) {
				if (
					addressGroup.controls[c].errors != null &&
					Object.keys(addressGroup.controls[c].errors).length > 0 &&
					!addressGroup.controls[c].pristine
				) {
					return true;
				}
			}
		}

		return false;
	};

	protected getProofRequirements = (): Map<string, ProofRequirementModel> => {
		const keys = ['phone', 'physical_address', 'mailing_address', 'ssoEmail'];

		const proofMap = new Map<string, ProofRequirementModel>();
		if (this.contactOptions) {
			keys.forEach((key) => {
				if (
					this.contactOptions['contact_field'][key]?.proof_requirement?.required
				) {
					const val =
						this.contactOptions['contact_field'][key]?.proof_requirement;
					if (val) {
						val['title'] = this.contactOptions['contact_field'][key].title;
					}
					proofMap.set(key, val);
				}
			});
		}

		return proofMap;
	};

	protected proofModal$ = (proofs: Map<string, ProofRequirementModel>) => {
		return this.uploadProofModalService.openModal$(proofs);
	};
}
